import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { MediaImage } from "@/components/ui/MediaImage";
import { VideoBlock } from "@/components/ui/VideoBlock";
import { useMediaByKey } from "@/content-context";
import type { GalleryItemDoc } from "@/content";
import type { MediaItem } from "@/data/media";
import { easeOut, lightboxBackdrop, lightboxPanel } from "@/lib/motion";
import { pillars } from "@/data/programs";

/**
 * The gallery: photographs and video in one browsable set.
 *
 * One collection rather than two tabs over two datasets. A visitor filtering
 * "everything from Bethanchowk in 2025" wants both kinds in the same list, and
 * two collections would have meant two queries, two sort orders and two places
 * for the consent rule to be forgotten.
 *
 * Every filter is built from the values the rows actually carry. Where HISCUS
 * has not published a location or a year, the row has none, the filter does not
 * offer one, and nothing is invented to fill the dropdown.
 *
 * Consent is enforced upstream: the public content service refuses to serve an
 * item whose consent status is pending or refused, whatever its publication
 * status says, so nothing filtered here has to know about it.
 *
 * Lightbox: focus moves in on open, Tab is trapped, Escape closes, arrow keys
 * move between items, and focus returns to the thumbnail that opened it. A
 * video opens in the same dialog and loads nothing from the video host until
 * somebody presses play.
 */

type Kind = "all" | "photo" | "video";

const PROGRAMME_LABELS: Record<string, string> = {
  ...Object.fromEntries(pillars.map((p) => [p.slug, p.shortTitle])),
  "charity-trek": "Charity Trek",
  volunteering: "Volunteering",
};

/** The rows arrive with the gallery page rather than with every page on the site. */
export function GalleryBrowser({ items: gallery }: { items: GalleryItemDoc[] }) {
  const byKey = useMediaByKey();

  const [kind, setKind] = useState<Kind>("all");
  const [programme, setProgramme] = useState("");
  const [project, setProject] = useState("");
  const [location, setLocation] = useState("");
  const [year, setYear] = useState("");
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const triggerRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const dialogRef = useRef<HTMLDivElement>(null);
  const lastTriggerIndex = useRef<number | null>(null);

  /** Only offer a filter value that at least one row actually carries. */
  const options = useMemo(() => {
    const unique = (pick: (item: GalleryItemDoc) => string | undefined) =>
      Array.from(new Set(gallery.map(pick).filter((v): v is string => Boolean(v)))).sort();
    return {
      programmes: unique((i) => i.programmeSlug),
      projects: unique((i) => i.projectSlug),
      locations: unique((i) => i.location),
      years: unique((i) => i.year).sort((a, b) => b.localeCompare(a)),
    };
  }, [gallery]);

  const visible = useMemo(
    () =>
      gallery.filter((item) => {
        if (kind !== "all" && item.kind !== kind) return false;
        if (programme && item.programmeSlug !== programme) return false;
        if (project && item.projectSlug !== project) return false;
        if (location && item.location !== location) return false;
        if (year && item.year !== year) return false;
        return true;
      }),
    [gallery, kind, programme, project, location, year],
  );

  const close = useCallback(() => {
    setOpenIndex(null);
    const index = lastTriggerIndex.current;
    if (index !== null) window.setTimeout(() => triggerRefs.current[index]?.focus(), 0);
  }, []);

  const step = useCallback(
    (delta: number) => {
      setOpenIndex((current) =>
        current === null || visible.length === 0 ? current : (current + delta + visible.length) % visible.length,
      );
    },
    [visible.length],
  );

  useEffect(() => {
    if (openIndex === null) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        step(1);
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        step(-1);
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll<HTMLElement>("button:not([disabled]), iframe, a[href]");
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const timer = window.setTimeout(
      () => dialogRef.current?.querySelector<HTMLElement>("button")?.focus(),
      60,
    );

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(timer);
    };
  }, [openIndex, close, step]);

  const active = openIndex === null ? null : (visible[openIndex] ?? null);
  const activeImage = active ? (byKey(active.mediaKey) ?? byKey(active.thumbnailKey)) : null;

  const reset = () => {
    setKind("all");
    setProgramme("");
    setProject("");
    setLocation("");
    setYear("");
    setOpenIndex(null);
  };

  const filtered = Boolean(programme || project || location || year || kind !== "all");
  /*
   * The native chevron sits hard against the right edge, so a select sized to
   * its own content ends up with the arrow on top of the label. `min-w` gives
   * every one of these room for its widest option plus the arrow, and they line
   * up as a row rather than as four different widths.
   */
  const selectClass =
    "select-field min-h-11 w-full rounded-full border border-line bg-white px-4 text-[0.875rem] text-ink transition-colors hover:border-line-strong sm:w-auto sm:min-w-[11rem]";

  return (
    <div>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by media type">
        {(
          [
            ["all", "All"],
            ["photo", "Photos"],
            ["video", "Videos"],
          ] as [Kind, string][]
        ).map(([value, label]) => {
          const selected = value === kind;
          return (
            <button
              key={value}
              type="button"
              aria-pressed={selected}
              onClick={() => {
                setKind(value);
                setOpenIndex(null);
              }}
              className={`inline-flex min-h-11 items-center gap-2 rounded-full border px-4 text-[0.875rem] font-medium transition-[background-color,border-color,color] duration-300 ${
                selected
                  ? "border-navy bg-navy text-white"
                  : "border-line bg-white text-ink/70 hover:border-line-strong hover:bg-mist hover:text-ink"
              }`}
            >
              {value === "video" ? <PlayMark /> : null}
              {label}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        {options.programmes.length > 0 ? (
          <label className="sr-only" htmlFor="gallery-programme">
            Programme
          </label>
        ) : null}
        {options.programmes.length > 0 ? (
          <select
            id="gallery-programme"
            value={programme}
            onChange={(e) => {
              setProgramme(e.target.value);
              setOpenIndex(null);
            }}
            className={selectClass}
          >
            <option value="">All programmes</option>
            {options.programmes.map((slug) => (
              <option key={slug} value={slug}>
                {PROGRAMME_LABELS[slug] ?? slug}
              </option>
            ))}
          </select>
        ) : null}

        {options.projects.length > 0 ? (
          <>
            <label className="sr-only" htmlFor="gallery-project">
              Project
            </label>
            <select
              id="gallery-project"
              value={project}
              onChange={(e) => {
                setProject(e.target.value);
                setOpenIndex(null);
              }}
              className={selectClass}
            >
              <option value="">All projects</option>
              {options.projects.map((slug) => (
                <option key={slug} value={slug}>
                  {slug}
                </option>
              ))}
            </select>
          </>
        ) : null}

        {options.locations.length > 0 ? (
          <>
            <label className="sr-only" htmlFor="gallery-location">
              Place
            </label>
            <select
              id="gallery-location"
              value={location}
              onChange={(e) => {
                setLocation(e.target.value);
                setOpenIndex(null);
              }}
              className={selectClass}
            >
              <option value="">All places</option>
              {options.locations.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </>
        ) : null}

        {options.years.length > 0 ? (
          <>
            <label className="sr-only" htmlFor="gallery-year">
              Year
            </label>
            <select
              id="gallery-year"
              value={year}
              onChange={(e) => {
                setYear(e.target.value);
                setOpenIndex(null);
              }}
              className={selectClass}
            >
              <option value="">All years</option>
              {options.years.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </>
        ) : null}

        {filtered ? (
          <button
            type="button"
            onClick={reset}
            className="inline-flex min-h-11 items-center px-2 text-[0.875rem] font-semibold text-navy underline underline-offset-4"
          >
            Clear filters
          </button>
        ) : null}
      </div>

      <p aria-live="polite" className="mt-4 text-[0.875rem] text-muted">
        Showing {visible.length} {visible.length === 1 ? "item" : "items"}
        {kind === "photo" ? " (photographs)" : kind === "video" ? " (video)" : ""}.
      </p>

      {visible.length === 0 ? (
        <p className="mt-8 max-w-2xl rounded-[16px] border border-line bg-mist p-6 text-muted">
          {kind === "video"
            ? "No video has been published yet. HISCUS has a YouTube channel, and no film from it has been nominated for this gallery, so nothing is shown rather than a placeholder."
            : "Nothing matches that combination. Clear the filters to see everything."}
        </p>
      ) : (
        /* CSS columns give a true masonry flow without measuring anything in JS. */
        <ul className="mt-8 columns-1 gap-5 sm:columns-2 lg:columns-3 [&>li]:mb-5 [&>li]:break-inside-avoid">
          {visible.map((item, index) => {
            const thumb = byKey(item.thumbnailKey) ?? byKey(item.mediaKey);
            return (
              <li key={item._id ?? `${item.kind}-${item.mediaKey ?? item.videoUrl}-${index}`}>
                <motion.figure
                  initial={{ opacity: 1, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.05, margin: "160px 0px" }}
                  transition={{ duration: 0.45, ease: easeOut }}
                >
                  <button
                    type="button"
                    ref={(el) => {
                      triggerRefs.current[index] = el;
                    }}
                    onClick={() => {
                      lastTriggerIndex.current = index;
                      setOpenIndex(index);
                    }}
                    className="group relative block w-full overflow-hidden rounded-[16px] bg-mist shadow-[0_1px_2px_rgba(12,29,51,0.05),0_14px_30px_-24px_rgba(12,29,51,0.28)] transition-[box-shadow,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-1 hover:shadow-[0_1px_2px_rgba(12,29,51,0.06),0_30px_54px_-30px_rgba(12,29,51,0.38)]"
                  >
                    {thumb ? (
                      <MediaImage
                        item={thumb}
                        sizes="(min-width: 1024px) 31vw, (min-width: 640px) 46vw, 100vw"
                        className="w-full transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.035]"
                        style={{ height: "auto" }}
                      />
                    ) : (
                      <span className="flex aspect-video w-full items-center justify-center bg-midnight text-white/60">
                        <PlayMark />
                      </span>
                    )}

                    {item.kind === "video" ? (
                      <span className="pointer-events-none absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-midnight/80 px-3 py-1 text-[0.75rem] font-semibold text-white backdrop-blur-sm">
                        <PlayMark />
                        Video
                      </span>
                    ) : null}

                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 bg-midnight/0 transition-colors duration-500 group-hover:bg-midnight/10"
                    />
                    <span className="sr-only">
                      {item.kind === "video" ? "Play video" : "Open larger view"}
                    </span>
                  </button>

                  <figcaption className="mt-3 px-0.5 text-[0.875rem] leading-relaxed text-muted">
                    {item.caption || item.title}
                    {item.location || item.year ? (
                      <span className="mt-1 block text-ink/60">
                        {[item.location, item.year].filter(Boolean).join(", ")}
                      </span>
                    ) : null}
                  </figcaption>
                </motion.figure>
              </li>
            );
          })}
        </ul>
      )}

      <AnimatePresence>
        {active ? (
          <motion.div
            variants={lightboxBackdrop}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-midnight/94 p-4 backdrop-blur-sm sm:py-8"
            onClick={(event) => {
              if (event.target === event.currentTarget) close();
            }}
          >
            <motion.div
              ref={dialogRef}
              variants={lightboxPanel}
              initial="hidden"
              animate="visible"
              exit="exit"
              role="dialog"
              aria-modal="true"
              aria-label={`${active.kind === "video" ? "Video" : "Image"} ${openIndex! + 1} of ${visible.length}: ${active.caption || active.title}`}
              className="my-auto w-full max-w-5xl"
            >
              <div className="flex items-center justify-between gap-4 pb-4">
                <p className="text-[0.8125rem] text-white/60">
                  {openIndex! + 1} of {visible.length}
                </p>
                <button
                  type="button"
                  onClick={close}
                  className="inline-flex h-11 items-center gap-2 rounded-full border border-white/25 px-5 text-[0.875rem] font-medium text-white transition-colors duration-300 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                >
                  Close
                  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4">
                    <path d="m6 6 12 12M18 6 6 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              {active.kind === "video" ? (
                <VideoBlock
                  key={active._id ?? active.videoUrl}
                  url={active.videoUrl}
                  poster={activeImage as MediaItem | null}
                  title={active.title || active.caption || "Video"}
                  radius={18}
                />
              ) : (
                <div className="relative aspect-[3/2] w-full overflow-hidden rounded-[18px] bg-black/30">
                  <MediaImage
                    item={activeImage}
                    fill
                    sizes="(min-width: 1024px) 66vw, 100vw"
                    className="object-contain"
                  />
                </div>
              )}

              <div className="mt-5 flex flex-col items-start justify-between gap-5 sm:flex-row">
                <div className="max-w-xl text-[0.9375rem] leading-relaxed text-white/85">
                  <p>{active.caption || active.title}</p>
                  {active.description ? (
                    <p className="mt-2 text-white/65">{active.description}</p>
                  ) : null}
                  {active.location || active.year || active.credit ? (
                    <p className="mt-1.5 text-white/55">
                      {[active.location, active.year, active.credit].filter(Boolean).join(" · ")}
                    </p>
                  ) : null}
                </div>
                <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
                  <button
                    type="button"
                    onClick={() => step(-1)}
                    className="inline-flex h-11 items-center justify-center rounded-full border border-white/25 px-4 text-[0.875rem] font-medium text-white transition-colors duration-300 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:px-5"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => step(1)}
                    className="inline-flex h-11 items-center justify-center rounded-full border border-white/25 px-4 text-[0.875rem] font-medium text-white transition-colors duration-300 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:px-5"
                  >
                    Next
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/** The one play mark on the site, at text size, drawn once. */
function PlayMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" className="h-3 w-3 fill-current">
      <path d="M5 3.5v9l8-4.5z" />
    </svg>
  );
}
